import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DynamicIcon } from '../components/icons';


const Highlight = ({ text, search }) => {
  if (!search) return <>{text}</>;
  const parts = text.split(new RegExp(`(${search})`, 'gi'));
  return (
    <>
      {parts.map((p, i) => 
        p.toLowerCase() === search.toLowerCase() ? (
          <span key={i} className="bg-amber-200/60 dark:bg-amber-500/30 text-amber-900 dark:text-amber-200 rounded-[2px] px-0.5">{p}</span>
        ) : p
      )}
    </>
  );
};

// ── Guide data ────────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'overview', icon: 'BookOpen', label: 'Overview',
    content: [
      { type: 'h3', text: 'Welcome to the AIDAS Enterprise Platform' },
      { type: 'p', text: 'The AIDAS Intelligence platform provides a unified, enterprise-grade environment to securely build, deploy, and manage your data-driven applications and AI workflows. Designed for scale, it abstracts complex infrastructure into intuitive visual studios, allowing teams to orchestrate powerful data models without writing code.' },
      { type: 'h3', text: 'Core Capabilities' },
      { type: 'bullets', items: [
        'Centralized Data Orchestration: Connect, clean, and model enterprise data securely while maintaining strict data governance.',
        'Agentic Insights: Empower users to convert natural language inquiries into accurate data visualizations and narrative summaries instantly.',
        'Visual Workflow Automation: Automate complex data pipelines and recurring processes via a drag-and-drop canvas.',
        'Multi-Tenant Architecture: Distribute curated Business Apps safely across isolated organizational workspaces.',
        'Proactive Monitoring: Configure automated schedules and threshold-based alerts to stay ahead of critical business events.'
      ]},
      { type: 'h3', text: 'Understanding Your Workspace' },
      { type: 'p', text: 'Access to platform modules is strictly governed by your organizational role and permissions. The platform dynamically renders only the tools, data models, and workflows that you are explicitly authorized to manage, ensuring a secure and focused working environment.' }
    ]
  },
  {
    id: 'nav', icon: 'LayoutDashboard', label: 'Navigation',
    content: [
      { type: 'h3', text: 'Workspace Organization' },
      { type: 'p', text: 'The platform interface is structured into core operational suites, ensuring that both creators and administrators can quickly access the tools they need. The primary interface is driven by the left-hand collapsible sidebar.' },
      { type: 'h3', text: 'Primary Interface Areas' },
      { type: 'table', headers: ['Area', 'Purpose'], rows: [
        ['Creator Studio', 'The authoring environment for building Flows, Dashboards, Business Apps, and using Agentic Insights.'],
        ['Data Settings', 'The configuration hub for managing Data Sources, Data Models, the Knowledge Base, and Scheduler.'],
        ['Administration', 'The management center for Roles, Users, Tenants, Billing, and Usage Tracking.']
      ]},
      { type: 'h3', text: 'Finding Your Assets' },
      { type: 'steps', items: [
        'Use the left sidebar to switch between operational suites and workspaces.',
        'Press Cmd/Ctrl + K from anywhere in the platform to launch the global command palette.',
        'Search for specific dashboards, workflows, data models, or users to navigate instantly.'
      ]},
      { type: 'h3', text: 'Role-Based Visibility' },
      { type: 'p', text: 'To maintain enterprise security, the platform employs strict visibility masking. If you do not see a specific module (such as Administration or Data Sources) in your sidebar, your current security profile does not grant you access to those features.' },
      { type: 'callout', variant: 'note', text: 'If you require access to restricted modules, please contact your workspace administrator to request a role modification.' }
    ]
  },
  {
    id: 'flows', icon: 'Workflow', label: 'Flows (Workflow Studio)',
    content: [
      { type: 'h3', text: 'Automating Business Logic' },
      { type: 'p', text: 'The Workflow Studio is the visual orchestration engine for defining automated processes. It replaces traditional code scripts with a secure, drag-and-drop canvas, allowing users to build scalable data pipelines, ETL processes, and automation logic visually.' },
      { type: 'h3', text: 'Designing a Flow' },
      { type: 'steps', items: [
        'Navigate to Creator Studio > Flows and click "New Flow".',
        'Drag processing nodes (such as Data Extractors, Transformers, or Output Webhooks) from the left library onto the central canvas.',
        'Draw connections between nodes to define the sequential dependency path and data flow.',
        'Select individual nodes to configure their specific parameters in the right-hand configuration panel.',
        'Click "Run" to execute a test and verify the output at each step.'
      ]},
      { type: 'h3', text: 'Execution & Monitoring' },
      { type: 'p', text: 'Once a Flow is published, it can be triggered manually, integrated into a Business App, or scheduled to run automatically. Flow executions, parallel processing, and scale orchestration are managed seamlessly by the platform, ensuring reliable delivery without manual infrastructure management.' },
      { type: 'callout', variant: 'tip', text: 'For extremely complex processing logic, custom enterprise nodes can be developed. Please contact the platform administration team for advanced node capabilities.' }
    ]
  },
  {
    id: 'ai', icon: 'BrainCircuit', label: 'Agentic Insights',
    content: [
      { type: 'h3', text: 'Conversational Analytics' },
      { type: 'p', text: 'Agentic Insights acts as your automated data assistant, securely converting natural language questions directly into accurate data visualizations against your semantic models. It eliminates the need for complex SQL queries while ensuring enterprise-grade accuracy.' },
      { type: 'h3', text: 'Asking Effective Questions' },
      { type: 'steps', items: [
        'Navigate to Creator Studio > Agentic Insights.',
        'Select your target Data Model from the dropdown selector.',
        'Type your question in plain English (e.g., "Show me total sales by region for Q3, compared to last year").',
        'Review the generated data table, chart, and the AI\'s analytical narrative.',
        'Use the "Pin" feature to save valuable insights directly to the Report Builder.'
      ]},
      { type: 'h3', text: 'Formatting & Refining Outputs' },
      { type: 'p', text: 'You can control how the AI structures its response by selecting formatting options from the input toolbar. Options include structured bullet points, executive summaries, or highlighted data metrics. If your question is ambiguous, the AI will present a Clarification Panel rather than guessing, allowing you to select your precise intent.' },
      { type: 'h3', text: 'Building Reports' },
      { type: 'p', text: 'The Report Builder allows you to compile pinned insights into a cohesive document. You can drag to reorder sections, edit the AI-generated narratives, and export the final report as a PDF, Excel file, or interactive web page.' },
      { type: 'callout', variant: 'note', text: 'The AI model utilized for processing your inquiries is dynamically selected and managed by the platform to optimize for speed, accuracy, and organizational policy.' }
    ]
  },
  {
    id: 'dashboards', icon: 'Layout', label: 'Dashboards',
    content: [
      { type: 'h3', text: 'Building Live Reports' },
      { type: 'p', text: 'Dashboards aggregate multiple data visualizations into cohesive, interactive business reports. They execute live, read-only queries against your semantic Data Models to ensure viewers always see the most current and accurate data.' },
      { type: 'h3', text: 'Authoring Workflow' },
      { type: 'steps', items: [
        'Navigate to Creator Studio > Dashboards and click "New Dashboard".',
        'Drag and drop data visualization widgets (such as Bar Charts, KPI Cards, and Data Tables) onto the layout canvas.',
        'Map each widget to a specific Data Model and visually configure its dimensions and measures.',
        'Set up global Filter Bars to allow viewers to interact with the data dynamically.',
        'Publish the dashboard to specific user groups or the entire organization.'
      ]},
      { type: 'h3', text: 'Best Practices for Dashboards' },
      { type: 'bullets', items: [
        'Maintain a clean layout by limiting the number of high-density widgets per page.',
        'Utilize global cross-filters to prevent redundant widget creation.',
        'Add Markdown widgets to provide context and explanations alongside complex visualizations.'
      ]}
    ]
  },
  {
    id: 'apps', icon: 'AppWindow', label: 'Business Apps',
    content: [
      { type: 'h3', text: 'Deploying Curated Experiences' },
      { type: 'p', text: 'Business Apps are curated software workspaces that bundle your published Dashboards and Flows together. This abstracts the complex Creator Studio behind a clean, unified interface, providing end-users with exactly the tools they need to perform their daily operations.' },
      { type: 'h3', text: 'Creating an App Bundle' },
      { type: 'steps', items: [
        'Navigate to Creator Studio > Business Apps and click "New App Bundle".',
        'Assign a name, description, and an identifying icon to the app.',
        'Select existing Dashboards and Flows from the organizational catalogue to include in the bundle.',
        'Define the application\'s internal sidebar navigation layout to optimize the user journey.',
        'Publish the app to specific tenant workspaces.'
      ]},
      { type: 'h3', text: 'Live Synchronization' },
      { type: 'p', text: 'Dashboards and Flows embedded inside Business Apps act as live pointers. Any modifications made to an asset within the Creator Studio will automatically synchronize and reflect inside the published Business App without requiring a manual redeployment.' }
    ]
  },
  {
    id: 'datastudio', icon: 'Database', label: 'Data Studio',
    content: [
      { type: 'h3', text: 'Securing Your Data Architecture' },
      { type: 'p', text: 'Data Sources and Models form the foundational architecture of the platform. Sources manage secure connections to your external databases, while Models act as the semantic layer, transforming those connections into business-friendly entities for reporting.' },
      { type: 'h3', text: 'Establishing Data Sources' },
      { type: 'steps', items: [
        'Navigate to Data Settings > Data Sources.',
        'Select your connection type (e.g., PostgreSQL, REST API) and securely input the required credentials.',
        'Test the connection to ensure platform connectivity, then save.'
      ]},
      { type: 'h3', text: 'Configuring the Semantic Layer (Models)' },
      { type: 'steps', items: [
        'Navigate to Data Settings > Data Models and click "New Model".',
        'Select the appropriate Data Source.',
        'Use the visual canvas to define table relationships, apply necessary filters, and configure calculated fields.',
        'Publish the Model to make it securely available for Dashboards and Agentic Insights.'
      ]},
      { type: 'callout', variant: 'important', text: 'Direct write-back capabilities to external databases are disabled by default to ensure organizational data governance and prevent untampered modifications.' }
    ]
  },
  {
    id: 'data', icon: 'HardDrive', label: 'Data Sources & Models',
    content: [
      { type: 'h3', text: 'Securing Your Data Connections' },
      { type: 'p', text: 'Data Sources and Models form the foundational architecture of the platform. Sources manage secure database connections, while Models map those connections into business-friendly semantic entities.' },
      { type: 'h3', text: 'Configuring the Semantic Layer' },
      { type: 'steps', items: [
        'Create a Data Source by configuring the connection details.',
        'Initialize a Data Model based on that source.',
        'Visually define table relationships.',
        'Publish the Model to make it available for Dashboards and Agentic Insights.'
      ]},
      { type: 'callout', variant: 'note', text: 'Please contact the support team for advanced configuration or custom translation assistance.' }
    ]
  },
  {
    id: 'kb', icon: 'Book', label: 'Knowledge Base',
    content: [
      { type: 'h3', text: 'Guiding the AI Engine' },
      { type: 'p', text: 'The Knowledge Base allows you to provide specific business context, acronyms, and calculation logic directly to the platform. By teaching the AI your exact organizational terminology, you ensure that Agentic Insights provides highly accurate, tailored answers rather than generic responses.' },
      { type: 'h3', text: 'Managing Terminology and Rules' },
      { type: 'steps', items: [
        'Navigate to Data Settings > Knowledge Base and open a Domain.',
        'Add a Metric to enforce a specific calculation (e.g., ensuring "Net Revenue" is always calculated correctly).',
        'Add a Rule to enforce mandatory filtering (e.g., "Always exclude test accounts from financial reports").',
        'Save your entries to update the Domain.'
      ]},
      { type: 'h3', text: 'Bulk Documentation Import' },
      { type: 'p', text: 'Instead of entering terms manually, you can upload existing data dictionaries, policy documents, or Excel templates. The platform will automatically parse the documents and extract relevant metrics and rules for your review.' },
      { type: 'callout', variant: 'tip', text: 'Certain operational behaviors, like cross-tenant synchronization, are managed automatically. It may take a few moments for newly added definitions to actively influence AI queries.' }
    ]
  },
  {
    id: 'scheduler', icon: 'Clock', label: 'Scheduler & Alerts',
    content: [
      { type: 'h3', text: 'Automated Operations Center' },
      { type: 'p', text: 'The Scheduler and Alerts module acts as your centralized command center for managing automated workflow executions and proactive KPI monitoring across the platform.' },
      { type: 'h3', text: 'Configuring Scheduled Automations' },
      { type: 'steps', items: [
        'Navigate to Data Settings > Scheduler.',
        'Select the Flow you wish to automate.',
        'Define the execution interval (e.g., daily, weekly, or specific recurring intervals).',
        'Save and activate the schedule.'
      ]},
      { type: 'h3', text: 'Setting Proactive Alerts' },
      { type: 'p', text: 'You can configure Alert Rules to trigger multi-channel notifications when specific business metrics breach defined thresholds. When setting alerts, ensure thresholds are meaningful to prevent alert fatigue among your operational teams.' },
      { type: 'callout', variant: 'note', text: 'Advanced configuration of custom webhook delivery endpoints is handled by the platform administration team.' }
    ]
  },
  {
    id: 'admin', icon: 'Shield', label: 'Administration',
    content: [
      { type: 'h3', text: 'Global Platform Management' },
      { type: 'p', text: 'The administration console serves as the central hub for managing organizational security policies, tenant workspace provisioning, and platform-wide user settings. Access to this module is strictly limited to authorized administrators.' },
      { type: 'h3', text: 'Core Administrative Actions' },
      { type: 'steps', items: [
        'Manage User Access: Invite new users, modify their assigned roles, and manage their active status.',
        'Tenant Provisioning: Create and manage isolated tenant workspaces to support distinct business units or clients.',
        'Security Oversight: Review active security policies and manage platform-wide compliance settings.'
      ]},
      { type: 'callout', variant: 'important', text: 'Please contact the support team for enterprise configuration assistance regarding custom Single Sign-On (SSO) integrations.' }
    ]
  },
  {
    id: 'roles', icon: 'Users', label: 'Roles & Permissions',
    content: [
      { type: 'h3', text: 'Granular Access Control' },
      { type: 'p', text: 'The platform utilizes Role-Based Access Control (RBAC) to ensure users only interact with authorized features and data. Roles act as scalable policy templates that bind to individual users, governing their entire platform experience.' },
      { type: 'h3', text: 'Assigning Permissions' },
      { type: 'steps', items: [
        'Navigate to Administration > Roles & Permissions.',
        'Review the standardized organizational roles (e.g., Admin, Creator, Viewer).',
        'Verify the feature access rights assigned to each role.',
        'Map individual users to the appropriate roles to enforce access policies.'
      ]},
      { type: 'p', text: 'Roles heavily influence platform visibility. A user assigned a "Viewer" role will not have access to the Creator Studio or Data Settings, ensuring they can only consume published Business Apps and Dashboards.' }
    ]
  },
  {
    id: 'credits', icon: 'Coins', label: 'Credit Management',
    content: [
      { type: 'h3', text: 'Resource Tracking' },
      { type: 'p', text: 'Platform operations—such as executing complex flows or generating Agentic Insights—consume compute credits from your organizational pool. This module allows administrators to track real-time consumption and configure threshold-based depletion alerts.' },
      { type: 'h3', text: 'Monitoring Usage' },
      { type: 'steps', items: [
        'Access Administration > Credit Management.',
        'Review current credit balances, usage rates, and historical burn down charts.',
        'Configure automated email alerts to notify billing contacts before the organizational pool is depleted.'
      ]},
      { type: 'callout', variant: 'note', text: 'Running out of credits may limit access to certain automated processing functionalities until the organizational pool is replenished. Please monitor burn rates closely.' }
    ]
  },
  {
    id: 'plans', icon: 'CreditCard', label: 'Plans & Tiers',
    content: [
      { type: 'h3', text: 'Subscription Management' },
      { type: 'p', text: 'Manage your organizational subscription level to unlock specific platform capabilities and increase your baseline operational capacity. Your active tier dictates access to advanced node libraries and computational resources.' },
      { type: 'h3', text: 'Managing Upgrades' },
      { type: 'bullets', items: [
        'Review active tier capabilities directly within the portal.',
        'Evaluate and initiate secure upgrade processes to permanently increase operational limits.'
      ]}
    ]
  },
  {
    id: 'usage', icon: 'Activity', label: 'Usage Tracking',
    content: [
      { type: 'h3', text: 'Compliance & Auditing' },
      { type: 'p', text: 'All user actions and significant platform events are securely logged for historical auditing. This provides comprehensive tracking of user interactions, performance metrics, and platform adoption across the organization.' },
      { type: 'h3', text: 'Reviewing Telemetry' },
      { type: 'steps', items: [
        'Navigate to Administration > Usage Tracking.',
        'Filter audit logs by specific users, tenants, or timeframes to investigate activities.',
        'Export audit logs to secure storage for external compliance reporting and archiving.'
      ]}
    ]
  },
  {
    id: 'relay', icon: 'Network', label: 'Hybrid Agent',
    content: [
      { type: 'h3', text: 'Secure Cloud Connectivity' },
      { type: 'p', text: 'The Hybrid Agent is an enterprise utility designed to securely connect your internal, on-premise data sources to the platform. It provides a secure bridge without requiring complex inbound firewall modifications or exposing internal networks to the public internet.' },
      { type: 'h3', text: 'Agent Deployment Overview' },
      { type: 'steps', items: [
        'Generate a secure Hybrid Agent installation token from the platform settings.',
        'Provide the token to your IT infrastructure team for internal deployment on your local servers.',
        'Verify the agent connection status directly in the platform portal to ensure connectivity.'
      ]},
      { type: 'callout', variant: 'note', text: 'Installation specifics, network requirements, and advanced agent configuration are managed securely by the platform administration team.' }
    ]
  },
  {
    id: 'support', icon: 'LifeBuoy', label: 'Support & Ticketing',
    content: [
      { type: 'h3', text: 'Direct Support Access' },
      { type: 'p', text: 'The Support module is your integrated communication channel for reporting issues, requesting operational guidance, and tracking resolution status directly with the platform support team.' },
      { type: 'h3', text: 'Requesting Assistance' },
      { type: 'steps', items: [
        'Navigate to Support & Reference > Support.',
        'Select "New Ticket" to initiate a request.',
        'Provide a detailed description of the issue, including workflow contexts and steps to reproduce.',
        'Submit the ticket and monitor the status dashboard for updates and resolution timelines.'
      ]}
    ]
  },
  {
    id: 'shortcuts', icon: 'Keyboard', label: 'Shortcuts & Glossary',
    content: [
      { type: 'h3', text: 'Keyboard Shortcuts' },
      { type: 'p', text: 'Enhance your platform efficiency by utilizing these global keyboard shortcuts.' },
      { type: 'table', headers: ['Shortcut', 'Action'], rows: [
        ['Cmd/Ctrl + K', 'Open global search palette for rapid navigation'],
        ['Cmd/Ctrl + Enter', 'Run / Execute active workflow or query'],
        ['Cmd/Ctrl + S', 'Save current progress'],
        ['Escape', 'Close active modal, panel, or overlay']
      ]},
      { type: 'h3', text: 'Glossary of Core Terms' },
      { type: 'table', headers: ['Term', 'Definition'], rows: [
        ['Flow', 'A visual pipeline of connected processing nodes automating business logic.'],
        ['Data Model', 'A semantic layer mapping raw external data schemas to business concepts.'],
        ['App Bundle', 'A curated software package containing dashboards and flows for end-users.'],
        ['Tenant', 'An organization or business unit with its own completely isolated workspace.'],
        ['Hybrid Agent', 'Secure connectivity software for bridging internal data sources to the platform.'],
        ['Credit', 'A unit of platform resource consumption utilized during intensive operations.']
      ]}
    ]
  }
];

const GROUPS = [
  { title: 'GETTING STARTED', items: ['overview', 'nav'] },
  { title: 'CREATOR STUDIO', items: ['flows', 'ai', 'dashboards', 'apps'] },
  { title: 'APP STUDIO', items: ['datastudio'] },
  { title: 'DATA SETTINGS', items: ['data', 'kb', 'scheduler'] },
  { title: 'ADMINISTRATION', items: ['admin', 'roles', 'credits', 'plans', 'usage', 'relay'] },
  { title: 'SUPPORT & REFERENCE', items: ['support'] }
];

const CALLOUT = {
  tip:      {bg:'bg-emerald-50 dark:bg-emerald-500/10',border:'border-emerald-500/20',icon:'Lightbulb',text:'text-emerald-800 dark:text-emerald-400',label:'Tip'},
  important:{bg:'bg-amber-50 dark:bg-amber-500/10',border:'border-amber-500/20',icon:'AlertTriangle',text:'text-amber-800 dark:text-amber-400',label:'Important'},
  note:     {bg:'bg-indigo-50 dark:bg-indigo-500/10',border:'border-indigo-500/20',icon:'Info',text:'text-indigo-800 dark:text-indigo-400',label:'Note'},
  caution:  {bg:'bg-rose-50 dark:bg-rose-500/10',border:'border-rose-500/20',icon:'AlertOctagon',text:'text-rose-800 dark:text-rose-400',label:'Caution'}
};

const Block = ({b, search}) => {
  if (!b) return null;
  if(b.type==='p')    return <p className="text-[15px] text-slate-600 dark:text-[#A1A1AA] leading-[1.8] mt-6 mb-6"><Highlight text={b.text} search={search} /></p>;
  
  if(b.type==='h3') {
    const id = b.text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return (
      <div className="pt-10 mt-10 border-t border-slate-100 dark:border-white/5 first:mt-0 first:pt-0 first:border-0">
        <h3 id={id} className="text-[20px] font-semibold tracking-tight text-slate-900 dark:text-slate-50 mb-5"><Highlight text={b.text} search={search} /></h3>
      </div>
    );
  }
  
  if(b.type==='bullets') return (
    <ul className="space-y-3 my-6 ml-2">
      {b.items.map((t,i) => (
        <li key={i} className="flex items-start gap-4 text-[15px] text-slate-600 dark:text-[#A1A1AA] leading-[1.7]">
          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-[#444] shrink-0"/>
          <span><Highlight text={t} search={search} /></span>
        </li>
      ))}
    </ul>
  );
  
  if(b.type==='steps') return (
    <div className="space-y-0 my-8 relative before:absolute before:inset-y-0 before:left-[11px] before:w-px before:bg-slate-100 dark:before:bg-[#222]">
      {b.items.map((t,i) => (
        <div key={i} className="relative flex items-start gap-5 pb-8 last:pb-0">
          <div className="shrink-0 w-6 h-6 rounded-full bg-white dark:bg-[#0A0A0A] border border-slate-200 dark:border-[#333] text-slate-500 dark:text-slate-400 text-[11px] font-bold flex items-center justify-center z-10 mt-1 shadow-sm">{i+1}</div>
          <div className="text-[15px] text-slate-600 dark:text-[#A1A1AA] leading-[1.7] pt-1"><Highlight text={t} search={search} /></div>
        </div>
      ))}
    </div>
  );
  
  if(b.type==='table') return (
    <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-[#222] my-8 bg-white dark:bg-[#0A0A0A] shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200 dark:border-[#222] bg-slate-50/50 dark:bg-[#111]">
              {b.headers.map((h,i) => <th key={i} className="px-6 py-3.5 text-[12px] font-semibold text-slate-500 dark:text-slate-400 tracking-wider uppercase">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-[#222]">
            {b.rows.map((r,ri) => (
              <tr key={ri} className="hover:bg-slate-50/50 dark:hover:bg-[#111] transition-colors">
                {r.map((c,ci) => (
                  <td key={ci} className={`px-6 py-4 text-[14px] leading-relaxed text-slate-600 dark:text-[#A1A1AA] ${ci===0 ? 'font-medium text-slate-900 dark:text-slate-200' : ''}`}><Highlight text={c} search={search} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  
  if(b.type==='callout') {
    const s=CALLOUT[b.variant]||CALLOUT.note;
    return (
      <div className="p-6 rounded-xl border border-slate-200 dark:border-[#222] bg-slate-50/50 dark:bg-[#111] my-8 shadow-sm">
        <div className="flex gap-4">
          <DynamicIcon name={s.icon} className="w-5 h-5 text-slate-700 dark:text-slate-300 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold text-slate-900 dark:text-slate-100 block mb-1.5 text-[15px]">{s.label}</span>
            <div className="text-[14px] leading-[1.7] text-slate-600 dark:text-[#A1A1AA]">
              <Highlight text={b.text} search={search} />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default function UserGuidePage({onClose}) {
  const [activeId,setActiveId] = useState('overview');
  const [search,setSearch] = useState('');
  
  useEffect(() => {
    const el = document.getElementById('guide-main-content');
    if (el) el.scrollTop = 0;
  }, [activeId]);
  
  const active = SECTIONS.find(s=>s.id===activeId) || SECTIONS[0];
  
  const searchQuery = search.toLowerCase().trim();
  const searchResults = [];
  if (searchQuery) {
    SECTIONS.forEach(s => {
      let foundInTitle = s.label.toLowerCase().includes(searchQuery);
      let matches = [];
      
      if (s.content) {
        s.content.forEach(b => {
          let texts = [];
          if (b.text) texts.push(b.text);
          if (b.items) texts.push(...b.items);
          if (b.headers) texts.push(...b.headers);
          if (b.rows) b.rows.forEach(r => texts.push(...r));
          
          texts.forEach(t => {
            const idx = t.toLowerCase().indexOf(searchQuery);
            if (idx !== -1) {
              const start = Math.max(0, idx - 40);
              const end = Math.min(t.length, idx + searchQuery.length + 40);
              let snippet = t.slice(start, end);
              if (start > 0) snippet = '...' + snippet;
              if (end < t.length) snippet = snippet + '...';
              matches.push(snippet);
            }
          });
        });
      }
      
      if (foundInTitle || matches.length > 0) {
        searchResults.push({ section: s, matches: matches.slice(0, 4) });
      }
    });
  }
  
  const idx = SECTIONS.findIndex(s=>s.id===activeId);
  const prevSection = idx > 0 ? SECTIONS[idx-1] : null;
  const nextSection = idx < SECTIONS.length-1 ? SECTIONS[idx+1] : null;
  
  const headers = active.content?.filter(b => b.type === 'h3').map(b => ({
    id: b.text.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    text: b.text
  })) || [];

  return(
    <div className="fixed inset-0 z-50 flex justify-center bg-white dark:bg-[#0A0A0A] font-sans selection:bg-indigo-500/30">
      <div className="flex w-full max-w-[1600px]">
        
        {/* Left Sidebar (Fixed Width) */}
        <aside className="w-[280px] hidden md:flex flex-col shrink-0 border-r border-slate-200 dark:border-white/5 py-8 px-6 overflow-y-auto">
          <div className="flex items-center gap-3 mb-8 px-2">
            <div className="p-1.5 bg-slate-900 dark:bg-white rounded shadow-sm">
              <DynamicIcon name="BookOpen" className="w-4 h-4 text-white dark:text-slate-900" />
            </div>
            <span className="font-semibold text-slate-900 dark:text-slate-50 text-[16px] tracking-tight">Platform Docs</span>
          </div>
          
          <div className="relative group mb-8 px-2">
            <DynamicIcon name="Search" className="absolute left-4 top-2.5 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e=>setSearch(e.target.value)} className="w-full pl-10 pr-10 py-2 text-[14px] bg-slate-50 dark:bg-[#111] border border-slate-200 dark:border-white/5 rounded-lg text-slate-900 dark:text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all shadow-sm" placeholder="Search..." />
            {search ? (
              <button onClick={() => setSearch('')} className="absolute right-3 top-2.5 p-0.5 text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                <DynamicIcon name="X" className="w-4 h-4" />
              </button>
            ) : (
              <div className="absolute right-2.5 top-2.5 text-[10px] font-medium text-slate-400 border border-slate-200 dark:border-white/10 rounded px-1.5 py-0.5">⌘K</div>
            )}
          </div>
          
             {search.trim() ? (
               <nav className="space-y-3 px-2 pb-6">
                 {searchResults.length === 0 && <div className="text-[13px] text-slate-500 px-3 mt-2">No results found.</div>}
                 {searchResults.map(res => (
                    <div key={res.section.id} className="space-y-1">
                      <button onClick={()=>{setActiveId(res.section.id);}} className={`w-full flex items-center gap-3 px-3 py-2 text-[14px] rounded-lg transition-colors ${activeId === res.section.id ? 'bg-slate-100 dark:bg-[#1A1A1A] text-slate-900 dark:text-slate-50 font-medium' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#111] hover:text-slate-900 dark:hover:text-slate-200'}`}>
                         <DynamicIcon name={res.section.icon} className={`w-4 h-4 shrink-0 ${activeId===res.section.id?'text-slate-900 dark:text-slate-50':'text-slate-400'}`} />
                         <span className="truncate text-left font-semibold">{res.section.label}</span>
                      </button>
                      {res.matches.map((m, i) => (
                        <div key={i} onClick={()=>{setActiveId(res.section.id);}} className="px-10 py-1 cursor-pointer text-[12px] text-slate-500 dark:text-slate-400 leading-snug line-clamp-2 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
                          <Highlight text={m} search={searchQuery} />
                        </div>
                      ))}
                    </div>
                 ))}
               </nav>
             ) : (
               <div className="space-y-8 pb-6 px-2">
                 {GROUPS.map(g => (
                   <div key={g.title}>
                     <h4 className="px-3 mb-3 text-[11px] font-semibold tracking-wider text-slate-400 dark:text-slate-500 uppercase">{g.title}</h4>
                     <nav className="space-y-1">
                       {g.items.map(id => {
                         const s = SECTIONS.find(x => x.id === id);
                         if (!s) return null;
                         const isActive = activeId === s.id;
                         return (
                           <button key={s.id} onClick={()=>{setActiveId(s.id);setSearch('');}} className={`w-full flex items-center gap-3 px-3 py-2 text-[14px] rounded-lg transition-colors ${isActive ? 'bg-slate-100 dark:bg-[#1A1A1A] text-slate-900 dark:text-slate-50 font-medium' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#111] hover:text-slate-900 dark:hover:text-slate-200'}`}>
                             <DynamicIcon name={s.icon} className={`w-4 h-4 shrink-0 ${isActive?'text-slate-900 dark:text-slate-50':'text-slate-400'}`} />
                             <span className="truncate text-left">{s.label}</span>
                           </button>
                         );
                       })}
                     </nav>
                   </div>
                 ))}
               </div>
             )}
        </aside>
        
        {/* Main Content Area */}
        <main id="guide-main-content" className="flex-1 min-w-0 overflow-y-auto relative bg-white dark:bg-[#0A0A0A]">
           <div className="sticky top-0 z-20 flex justify-end p-4 md:absolute md:top-6 md:right-6 md:p-0">
             <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 text-slate-600 dark:text-slate-300 transition-colors text-[13px] font-medium shadow-sm">
               <DynamicIcon name="ArrowLeft" className="w-4 h-4" />
               Back to Platform
             </button>
           </div>
           
           {active.iframe ? (
              <iframe key={activeId} src={active.iframe + (searchQuery ? '?search=' + encodeURIComponent(searchQuery) : '')} className="w-full h-full min-h-[100vh] border-0" title={active.label} />
           ) : (
             <div className="w-full mx-auto px-8 py-12 lg:px-16 xl:px-24 2xl:px-32 lg:py-20 flex flex-col">
               <div className="mb-14">
                 
                 <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-tight">{active.label}</h1>
               </div>
               
               <div className="prose-container w-full max-w-none">
                 <AnimatePresence mode="wait">
                   <motion.div key={activeId} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-10}} transition={{duration:0.2}}>
                      {active.content.map((b,i) => <Block key={i} b={b} search={searchQuery} />)}
                   </motion.div>
                 </AnimatePresence>
               </div>
               
               <div className="flex flex-col sm:flex-row items-center gap-4 pt-12 mt-16 border-t border-slate-200 dark:border-white/10 pb-12 w-full">
                  {prevSection ? (
                    <button onClick={()=>setActiveId(prevSection.id)} className="w-full sm:w-1/2 text-left p-6 rounded-2xl border border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/20 hover:shadow-sm dark:hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-all group">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Previous</div>
                      <div className="text-[15px] font-semibold text-slate-900 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 flex items-center gap-2">
                        <DynamicIcon name="ArrowLeft" className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> {prevSection.label}
                      </div>
                    </button>
                  ) : <div className="hidden sm:block sm:w-1/2" />}
                  {nextSection ? (
                    <button onClick={()=>setActiveId(nextSection.id)} className="w-full sm:w-1/2 text-right p-6 rounded-2xl border border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/20 hover:shadow-sm dark:hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-all group">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Next</div>
                      <div className="text-[15px] font-semibold text-slate-900 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 flex items-center justify-end gap-2">
                        {nextSection.label} <DynamicIcon name="ArrowRight" className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </button>
                  ) : <div className="hidden sm:block sm:w-1/2" />}
               </div>
               
               <div className="mt-8 text-center text-[12px] text-slate-400 dark:text-slate-500 pb-8">
                 &copy; 2026 AIDAS Intelligence platform. All rights reserved.
               </div>
             </div>
           )}
        </main>
        
        {/* Right Sidebar (On This Page) */}
        {!active.iframe && headers.length > 0 && (
        <aside className="w-[240px] hidden xl:flex flex-col shrink-0 py-20 px-8 overflow-y-auto border-l border-slate-200 dark:border-white/5 bg-white dark:bg-[#0A0A0A]">
          <div className="sticky top-20">
            <h4 className="text-[11px] font-semibold text-slate-900 dark:text-slate-100 uppercase tracking-wider mb-5">On This Page</h4>
            <nav className="space-y-3">
               {headers.map(h => (
                  <a key={h.id} href={`#${h.id}`} className="block text-[13px] text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors leading-snug">{h.text}</a>
               ))}
            </nav>
          </div>
        </aside>
        )}
      </div>
    </div>
  );
}
